import { Scale } from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
	{
		title: "1. Acceptance of Terms",
		content:
			'By accessing or using the Heliocentric Astrology application ("App"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our App. These terms apply to all visitors, users, and others who access or use the App.',
	},
	{
		title: "2. Description of Service",
		content:
			"Heliocentric Astrology provides heliocentric astrological chart calculations, AI-powered interpretations, planetary position data, and related astrological content. The App is designed for entertainment and educational purposes. Astrological readings should not be used as a substitute for professional advice in medical, legal, financial, or other domains.",
	},
	{
		title: "3. User Accounts",
		content:
			"You must create an account to access most features. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate, current, and complete information when creating your account. You may not use another person's account without their permission.",
	},
	{
		title: "4. Subscription Plans & Payments",
		content:
			"We offer both free and premium subscription tiers. Premium features require a paid subscription. All payments are processed securely through Stripe. Subscription fees are billed in advance and are non-refundable except as required by law. We reserve the right to change pricing with 30 days' notice.",
	},
	{
		title: "5. Credits System",
		content:
			"Premium users receive credits that unlock unique birth-data charts for full interpretations. Reports for unlocked charts are included at no extra cost. Credits are non-transferable, have no cash value, and expire when your subscription ends. Unused credits do not roll over to the next billing period unless otherwise stated.",
	},
	{
		title: "6. AI-Generated Content",
		content:
			"Our App uses artificial intelligence to generate astrological interpretations. AI-generated content is provided for entertainment purposes only. We do not guarantee the accuracy, completeness, or reliability of AI-generated interpretations. You should not make important life decisions based solely on AI-generated astrological content.",
	},
	{
		title: "7. Intellectual Property",
		content:
			"The App and its original content, features, and functionality are owned by Heliocentric Astrology and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our App without our written permission.",
	},
	{
		title: "8. User Conduct",
		content:
			"You agree not to use the App for any unlawful purpose or in any way that could damage, disable, or impair the App. You may not attempt to gain unauthorized access to any part of the App, other accounts, or computer systems. Harassment, abuse, or harmful behavior toward other users is strictly prohibited.",
	},
	{
		title: "9. Disclaimer of Warranties",
		content:
			'The App is provided on an "AS IS" and "AS AVAILABLE" basis without any warranties of any kind. We do not warrant that the App will be uninterrupted, error-free, or completely secure. Astrological content is for entertainment purposes and we make no claims regarding its accuracy or predictive value.',
	},
	{
		title: "10. Limitation of Liability",
		content:
			"To the maximum extent permitted by law, Heliocentric Astrology shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, the App. Our total liability to you for any claims shall not exceed the amount you paid for the App in the 12 months preceding the claim.",
	},
	{
		title: "11. Account Deletion",
		content:
			"You may request deletion of your account at any time from the Profile section of the App. Upon deletion, your account and associated data will be soft-deleted immediately and permanently removed from our systems within 30 days. Certain data may be retained longer if required by law.",
	},
	{
		title: "12. Termination",
		content:
			"We reserve the right to suspend or terminate your account at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, third parties, or the integrity of the App.",
	},
	{
		title: "13. Changes to Terms",
		content:
			"We reserve the right to modify these Terms at any time. We will provide notice of significant changes via email or in-app notification. Your continued use of the App after changes constitutes your acceptance of the new Terms.",
	},
	{
		title: "14. Governing Law",
		content:
			"These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.",
	},
	{
		title: "15. Contact Information",
		content:
			"If you have questions about these Terms and Conditions, please contact us at legal@heliocentricapp.com.",
	},
];

const TermsConditions = () => {
	return (
		<div className="min-h-screen bg-slate-50 py-10 px-4">
			<div className="max-w-3xl mx-auto">
				{/* Header card */}
				<div
					className="rounded-2xl p-6 mb-8 text-white"
					style={{
						background: "linear-gradient(135deg, #312e81 0%, #4f46e5 100%)",
					}}
				>
					<div className="flex items-center gap-3 mb-3">
						<div className="bg-white/15 rounded-xl p-2.5">
							<Scale size={22} className="text-yellow-400" />
						</div>
						<h1 className="text-2xl font-bold">Terms &amp; Conditions</h1>
					</div>
					<p className="text-white/70 text-sm mb-2">Last updated: March 2026</p>
					<p className="text-white/85 text-sm leading-relaxed">
						Please read these Terms and Conditions carefully before using the
						Heliocentric Astrology application.
					</p>
				</div>

				{/* Sections */}
				<div className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100 mb-6">
					{sections.map(({ title, content }) => (
						<div key={title} className="px-6 py-5">
							<h2 className="text-sm font-bold text-slate-900 mb-2">{title}</h2>
							<p className="text-sm text-slate-500 leading-relaxed">{content}</p>
						</div>
					))}
				</div>

				{/* Footer notice */}
				<div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex gap-3 items-start mb-8">
					<span className="text-violet-500 mt-0.5 text-base">✓</span>
					<p className="text-xs text-violet-800 leading-relaxed">
						By creating an account or continuing to use Heliocentric Astrology, you
						confirm that you have read and agree to these Terms and Conditions.
					</p>
				</div>

				{/* Navigation links */}
				<div className="flex gap-4 text-sm text-slate-500 justify-center">
					<Link to="/" className="hover:text-slate-800 transition-colors">
						← Back to Home
					</Link>
					<span className="text-slate-300">|</span>
					<Link
						to="/privacy-policy"
						className="hover:text-slate-800 transition-colors"
					>
						Privacy Policy
					</Link>
				</div>
			</div>
		</div>
	);
};

export default TermsConditions;
